import { assertUnreachable } from '@sgme/fp';
import type { QuoteAck } from 'models/quote/QuoteAck';
import type { QuoteRequestPayload } from 'models/quote/QuoteRequestPayload';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { QuoteType } from 'state/quotes/infos/infos.model';
import { WorkspaceType } from 'state/workspaces/model';
import { IHttpClient } from 'utils/http/httpClient.model';

export type QuotingWorkspaceType = Extract<WorkspaceType, 'static' | 'dynamic' | 'ordersBasket' | 'essential'>;

export interface RequestQuoteApi {
  requestQuote(payload: QuoteRequestPayload, screen: QuotingWorkspaceType, type: QuoteType): Observable<QuoteAck>;
}

function getQuoteUrlWithParams(screen: QuotingWorkspaceType, quoteType: QuoteType): string {
  return ['api/v1/quote?', getScreenParam(screen), getQuoteTypeParams(quoteType)].join('');
}

function getScreenParam(screen: QuotingWorkspaceType): string {
  switch (screen) {
    case 'static':
      return 'screen=newStatic&';
    case 'essential':
      return 'screen=essential&';
    case 'dynamic':
      return 'screen=newDynamic&';
    case 'ordersBasket':
      return 'screen=ordersBasket&';
    default:
      return assertUnreachable(screen, 'Unhandled screen');
  }
}

function getQuoteTypeParams(quoteType: QuoteType): string {
  switch (quoteType) {
    case 'Indicative':
      return 'mode=indicative&regulation=disabled';
    case 'AdvancedIndicative':
      return 'mode=indicative&regulation=disabled&bypassTradingRulesCheck=true';
    case 'Tradable':
      return 'mode=tradable&regulation=enabled&requestTradeAuth=true';
    case 'AdvancedTradable':
      return 'mode=tradable&regulation=enabled&requestTradeAuth=true&bypassTradingRulesCheck=true';
    case 'Executable':
      return 'mode=tradable&regulation=enabled';
    case 'AdvancedExecutable':
      return 'mode=tradable&regulation=enabled&bypassTradingRulesCheck=true';
    case 'ExecOnly':
      return 'mode=tradable&regulation=execOnly&requestTradeAuth=true';
    case 'AdvancedExecOnly':
      return 'mode=tradable&regulation=execOnly&requestTradeAuth=true&bypassTradingRulesCheck=true';
    case 'BookWithExecution':
    case 'BookWithoutExecution':
      throw new Error(`${quoteType} not supported`);
    default:
      return assertUnreachable(quoteType, 'Unhandled quote type');
  }
}

export function quoteRequestApi(http: IHttpClient, ackTimeoutInMillis: number): RequestQuoteApi {
  return {
    requestQuote(payload, screen, type) {
      return http
        .postJSON<QuoteAck>({
          url: getQuoteUrlWithParams(screen, type),
          body: payload,
          tryParseErrorPayload: true,
          timeOut: ackTimeoutInMillis,
        })
        .pipe(map(r => r.payload));
    },
  };
}
